import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, LayoutGrid, List, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const STATUSES = ["bozza", "aperta", "in_lavorazione", "attesa_cliente", "revisione", "chiusa", "archiviata"];
const TYPES = ["contrattuale", "consulenza", "contenzioso", "mna", "nis2", "fiscale", "altro"];
const PRIORITIES = ["bassa", "normale", "alta", "urgente"];

const statusColors = {
  bozza: "bg-slate-100 text-slate-700", aperta: "bg-blue-100 text-blue-700",
  in_lavorazione: "bg-amber-100 text-amber-700", attesa_cliente: "bg-purple-100 text-purple-700",
  revisione: "bg-indigo-100 text-indigo-700", chiusa: "bg-emerald-100 text-emerald-700",
  archiviata: "bg-gray-200 text-gray-600",
};
const prioColors = {
  bassa: "bg-slate-100 text-slate-600", normale: "bg-blue-50 text-blue-600",
  alta: "bg-orange-100 text-orange-700", urgente: "bg-red-100 text-red-700",
};

function NewPracticeDialog({ contacts, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", type: "consulenza", priority: "normale",
    status: "aperta", client_contact_id: "", deadline: "", progress_pct: 0,
  });
  const submit = async () => {
    if (!form.title.trim()) { toast.error("Titolo richiesto"); return; }
    try {
      await api.post("/practices", { ...form, progress_pct: parseInt(form.progress_pct || 0), client_contact_id: form.client_contact_id || null, deadline: form.deadline || null });
      toast.success("Pratica creata");
      setOpen(false);
      onCreated();
    } catch (e) { toast.error("Errore creazione pratica"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-navy-800 hover:bg-navy-900 text-white" data-testid="new-practice-btn"><Plus className="w-4 h-4 mr-1" />Nuova Pratica</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Nuova Pratica</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 space-y-1.5">
            <Label>Titolo *</Label>
            <Input value={form.title} onChange={(e)=>setForm({...form, title: e.target.value})} data-testid="practice-title-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Tipo</Label>
            <Select value={form.type} onValueChange={(v)=>setForm({...form, type: v})}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Priorità</Label>
            <Select value={form.priority} onValueChange={(v)=>setForm({...form, priority: v})}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Cliente</Label>
            <Select value={form.client_contact_id || "_none"} onValueChange={(v)=>setForm({...form, client_contact_id: v === "_none" ? "" : v})}>
              <SelectTrigger><SelectValue placeholder="Seleziona contatto"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">— nessuno —</SelectItem>
                {contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Scadenza</Label>
            <Input type="date" value={form.deadline} onChange={(e)=>setForm({...form, deadline: e.target.value})} />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label>Descrizione</Label>
            <Textarea rows={3} value={form.description} onChange={(e)=>setForm({...form, description: e.target.value})} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Annulla</Button>
          <Button className="bg-navy-800 hover:bg-navy-900" onClick={submit} data-testid="practice-save-btn">Crea Pratica</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Pratiche() {
  const [practices, setPractices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [filter, setFilter] = useState({ q: "", status: "", priority: "", type: "" });
  const [view, setView] = useState("list");
  const navigate = useNavigate();

  const load = async () => {
    const params = new URLSearchParams();
    if (filter.q) params.set("q", filter.q);
    if (filter.status) params.set("status", filter.status);
    if (filter.priority) params.set("priority", filter.priority);
    if (filter.type) params.set("type", filter.type);
    const { data } = await api.get(`/practices?${params}`);
    setPractices(data);
  };

  useEffect(() => { load(); }, [filter]);
  useEffect(() => { api.get("/contacts").then(r => setContacts(r.data)); }, []);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-navy-900">Pratiche</h1>
          <p className="text-sm text-slate-500 mt-1">{practices.length} pratiche · gestisci tutte le pratiche dello studio</p>
        </div>
        <div className="flex gap-2">
          <Tabs value={view} onValueChange={setView}>
            <TabsList><TabsTrigger value="list" data-testid="view-list"><List className="w-4 h-4" /></TabsTrigger><TabsTrigger value="kanban" data-testid="view-kanban"><LayoutGrid className="w-4 h-4" /></TabsTrigger></TabsList>
          </Tabs>
          <NewPracticeDialog contacts={contacts} onCreated={load} />
        </div>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <Input className="pl-9" placeholder="Cerca per titolo o protocollo" value={filter.q} onChange={(e)=>setFilter({...filter, q: e.target.value})} data-testid="practices-search" />
          </div>
          <Select value={filter.status || "_all"} onValueChange={(v)=>setFilter({...filter, status: v === "_all" ? "" : v})}>
            <SelectTrigger><SelectValue placeholder="Stato"/></SelectTrigger>
            <SelectContent><SelectItem value="_all">Tutti gli stati</SelectItem>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filter.priority || "_all"} onValueChange={(v)=>setFilter({...filter, priority: v === "_all" ? "" : v})}>
            <SelectTrigger><SelectValue placeholder="Priorità"/></SelectTrigger>
            <SelectContent><SelectItem value="_all">Tutte le priorità</SelectItem>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filter.type || "_all"} onValueChange={(v)=>setFilter({...filter, type: v === "_all" ? "" : v})}>
            <SelectTrigger><SelectValue placeholder="Tipo"/></SelectTrigger>
            <SelectContent><SelectItem value="_all">Tutti i tipi</SelectItem>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </Card>

      {view === "list" && (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-5 py-2.5">Protocollo</th>
                  <th className="text-left px-5 py-2.5">Titolo</th>
                  <th className="text-left px-5 py-2.5">Tipo</th>
                  <th className="text-left px-5 py-2.5">Stato</th>
                  <th className="text-left px-5 py-2.5">Priorità</th>
                  <th className="text-left px-5 py-2.5">Scadenza</th>
                  <th className="text-left px-5 py-2.5 w-32">Avanzamento</th>
                </tr>
              </thead>
              <tbody>
                {practices.map(p => (
                  <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/60 cursor-pointer" onClick={() => navigate(`/pratiche/${p.id}`)} data-testid={`practice-row-${p.id}`}>
                    <td className="px-5 py-3 font-mono text-xs text-slate-600">{p.numero_protocollo}</td>
                    <td className="px-5 py-3 text-navy-900 font-medium">{p.title}</td>
                    <td className="px-5 py-3 text-slate-600 capitalize">{p.type}</td>
                    <td className="px-5 py-3"><Badge className={`${statusColors[p.status]} border-0`}>{p.status.replace("_"," ")}</Badge></td>
                    <td className="px-5 py-3"><Badge className={`${prioColors[p.priority]} border-0`}>{p.priority}</Badge></td>
                    <td className="px-5 py-3 text-slate-600">{p.deadline || "—"}</td>
                    <td className="px-5 py-3"><Progress value={p.progress_pct} className="h-1.5" /></td>
                  </tr>
                ))}
                {practices.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-12">Nessuna pratica trovata</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {view === "kanban" && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {STATUSES.filter(s => s !== "archiviata").map(s => (
            <div key={s} className="bg-white border border-slate-200 rounded-md p-3 min-h-[200px]">
              <div className="text-xs font-semibold uppercase text-slate-500 mb-3 flex items-center justify-between">
                <span>{s.replace("_"," ")}</span>
                <span className="text-slate-400">{practices.filter(p=>p.status===s).length}</span>
              </div>
              <div className="space-y-2">
                {practices.filter(p=>p.status===s).map(p => (
                  <div key={p.id} onClick={()=>navigate(`/pratiche/${p.id}`)} className="p-3 bg-slate-50 hover:bg-white border border-slate-200 rounded cursor-pointer transition-colors" data-testid={`kanban-card-${p.id}`}>
                    <div className="font-mono text-[10px] text-slate-500">{p.numero_protocollo}</div>
                    <div className="text-sm font-medium text-navy-900 mt-0.5 line-clamp-2">{p.title}</div>
                    <div className="flex items-center gap-1 mt-2">
                      <Badge className={`${prioColors[p.priority]} border-0 text-[10px]`}>{p.priority}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
